import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DredgingProjectUsageResourceModel } from '../../../core/_models/operation';
import { DredgingService } from '../../../core/_services/dredging.service';
import { LabelI18nService } from '../../../core/_services/label-i18n.service';

export interface DialogDredgingEquipmentComponentResponseDataModel {
  dredge: DredgingProjectUsageResourceModel;
  selectedFile: File | null;
}

@Component({
  selector: 'app-dialog-dredging-equipment',
  templateUrl: './dialog-dredging-equipment.component.html',
  styleUrls: ['./dialog-dredging-equipment.component.scss']
})
export class DialogDredgingEquipmentComponent implements OnInit {
  fuelName?: string;
  co2PerLiter?: number;

  openMobilization = true;
  openExecution = true;
  openEmissionFactors = true;
  openMobilizationComment = true;
  openExecutionComment = true;

  selectedFile: File | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogDredgingEquipmentComponent>,
    private dredgingService: DredgingService,
    private labelI18n: LabelI18nService,
    @Inject(MAT_DIALOG_DATA) public data: DredgingProjectUsageResourceModel
  ) {}

  ngOnInit(): void {
    if (!this.data.displayName) {
      this.data.displayName = '';
    }
    if (!this.data.bringPhaseFuelConsumption) {
      this.data.bringPhaseFuelConsumption = 0;
    }
    if (!this.data.execPhaseDuration) {
      this.data.execPhaseDuration = 0;
    }
    if (!this.data.execPhaseFuelConsumption) {
      this.data.execPhaseFuelConsumption = 0;
    }
    if (!this.data.bringPhaseComment) {
      this.data.bringPhaseComment = '';
    }
    if (!this.data.execPhaseComment) {
      this.data.execPhaseComment = '';
    }

    this.openMobilizationComment = !!this.data.bringPhaseComment && this.data.bringPhaseComment !== '';
    this.openExecutionComment = !!this.data.execPhaseComment && this.data.execPhaseComment !== '';

    if (this.data.selectedFuelName && this.data.selectedFuelCo2PerLiter) {
      this.fuelName = this.data.selectedFuelName;
      this.co2PerLiter = this.data.selectedFuelCo2PerLiter;
    } else {
      this.dredgingService.getDefaultFuel().subscribe((fuel) => {
        this.fuelName = fuel.displayName;
        this.co2PerLiter = fuel.co2PerLiter;
      });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  calculateMobilizationEmissions(): string {
    let bringPhaseFuelConsumption = this.data.bringPhaseFuelConsumption ?? 0;
    if (isNaN(bringPhaseFuelConsumption) || this.co2PerLiter === undefined) return '0.000';
    this.data.bringPhaseCo2Eq = bringPhaseFuelConsumption * this.co2PerLiter;
    return this.data.bringPhaseCo2Eq.toFixed(3);
  }

  calculateExecutionEmissions(): string {
    let execPhaseDuration = this.data.execPhaseDuration ?? 0;
    let execPhaseFuelConsumption = this.data.execPhaseFuelConsumption ?? 0;
    if (isNaN(execPhaseDuration) || isNaN(execPhaseFuelConsumption) || this.co2PerLiter === undefined) return '0.000';
    const totalFuel = execPhaseDuration * execPhaseFuelConsumption;
    this.data.execPhaseCo2Eq = totalFuel * this.co2PerLiter;
    return this.data.execPhaseCo2Eq.toFixed(3);
  }

  getTotalFuelConsumption(): string {
    const mobilization = this.data.bringPhaseFuelConsumption ?? 0;
    const execution = (this.data.execPhaseDuration ?? 0) * (this.data.execPhaseFuelConsumption ?? 0);
    this.data.totalFuelConsumption = mobilization + execution;
    return this.data.totalFuelConsumption.toFixed(0);
  }

  getTotalEmissions(): string {
    const mobilization = parseFloat(this.calculateMobilizationEmissions());
    const execution = parseFloat(this.calculateExecutionEmissions());
    this.data.totalCo2Eq = mobilization + execution;
    return this.data.totalCo2Eq.toFixed(3);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.selectedFile = file;
      this.data.fileName = file.name;
      input.value = '';
    }
  }

  removeJustificatif(): void {
    this.selectedFile = null;
    this.data.fileName = '';
    this.data.fileDescription = '';
  }

  formatEmissionFactor(): string {
    return this.co2PerLiter === undefined ? '' : this.labelI18n.exponential(this.co2PerLiter, 5);
  }

  onSave(): void {
    let retVal: DialogDredgingEquipmentComponentResponseDataModel = {
      dredge: this.data,
      selectedFile: this.selectedFile
    };
    this.dialogRef.close(retVal);
  }
}
